import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Field, TextInput } from '@bcecloud/portal-ui';
import { AuthCard } from '@/components/AuthCard';
import { forgotPassword } from '@/lib/auth';

/**
 * Forgot password page — sends a reset link via email.
 */
export function ForgotPasswordPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Requests the reset link. Always reports success, so the form cannot be
   * used to probe which addresses have accounts.
   *
   * @param event - The submit event.
   */
  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
    } catch {
      // Always show success to prevent email enumeration.
    } finally {
      setSent(true);
      setLoading(false);
    }
  }

  return (
    <AuthCard
      title="Reset password"
      subtitle={
        sent
          ? 'Check your inbox for the next steps.'
          : 'Enter your email and we’ll send you a reset link.'
      }
    >
      {sent ? null : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
          <Field label="Email" htmlFor="email">
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </Field>

          <Button type="submit" variant="primary" disabled={loading} className="w-full">
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}

      <p className="mt-5 text-center text-portal-small">
        <Link
          to="/auth/login"
          className="font-bold text-brand-link underline-offset-2 hover:underline"
        >
          Back to sign in
        </Link>
      </p>
    </AuthCard>
  );
}
